import { createRecordInfo, RecordInfo } from "../../common/models/RecordInfo";
import { CustomerLevel } from "../../customers/models/CustomerModel";
import { BooleanOption } from "../../home/models/OtherTypes";
import { SortModel } from "../../home/models/PageTypes";
import { PictureModel } from "../../pictures/models/PictureModel";
import { MenuFilterModel } from "./CategoryModel";
import { EarningPointModel } from "./EarningPointModel";
import { createProductFeatureModel, ProductFeatureModel } from "./NewProductModel";

export enum PriceDisplayType {
    hide,
    show,
    xOne,
    xTwo,
    xThree,
    xFour,
    xFive
}

export enum ProductSearchOption {
    contains,
    exact,
    lineId,
    groupId,
    barcode
}

export enum ProductFeatureType {
    none,
    specialSale,
    topRate,
    topSale,
    newArrival,
    choice
}

export enum SearchOption {
    contains, exact, lineId, groupId, barcode
}

export interface ShortLineModel {
    id: number;
    productId: number;
    categoryId: number;
    active: boolean;
    approved: boolean;
    deleted: boolean;
    level: CustomerLevel;
    activeCount: number;
    customerSeen: boolean;
    inStock: boolean;
    name: string;
    style: string;
    created: Date;
    lastModified: Date;
    priceDisplayType: PriceDisplayType;
    newArrivalDate: Date;

    choice: boolean;
    newArrival: boolean;
    specialSale: boolean;
    topRate: boolean;
    topSale: boolean;
    alwaysApproved: boolean;
    alwaysCostConfirmed: boolean;

    groupId: number;
    productName: string;
    brandId: number;
    brand: string;
    sizeIds: string;
    barcodes: string;
    categoryIds: string;
    pictureTag: string;
    pointCount: number;

    imageUrl: string;
    printInfo: ProductPrintModel;
    minPrice: string;
    maxPrice: string;
    isFavorite: boolean;
    pointRate: number;
}

export const createShortProductLineModel = (): ShortLineModel => ({
    id: 0,
    productId: 0,
    categoryId: 0,
    active: false,
    approved: false,
    deleted: false,
    level: CustomerLevel.invited,
    activeCount: 0,
    customerSeen: false,
    inStock: false,
    name: "",
    style: "",
    created: new Date(0),
    lastModified: new Date(0),
    priceDisplayType: PriceDisplayType.show,
    newArrivalDate: new Date(0),

    choice: false,
    newArrival: false,
    specialSale: false,
    topRate: false,
    topSale: false,
    alwaysApproved: false,
    alwaysCostConfirmed: false,

    groupId: 0,
    productName: "",
    brandId: 0,
    brand: "",
    sizeIds: "",
    barcodes: "",
    categoryIds: "",
    pictureTag: "",
    pointCount: 0,

    imageUrl: "",
    printInfo: createProductPrintModel(),
    minPrice: "",
    maxPrice: "",
    isFavorite: false,
    pointRate: 0
});

export interface ProductPrintModel {
    id: number;
    price: string;
    wholePrice: string;
    decimalPrice: string;
    originalPrice: string;
    saveDescription: string;
    freeDelivery: boolean;
    inStock: boolean;
    size: string;
    code: string;
}

export const createProductPrintModel = (): ProductPrintModel => ({
    id: 0,
    price: "",
    wholePrice: "",
    decimalPrice: "",
    originalPrice: "",
    saveDescription: "",
    freeDelivery: false,
    inStock: false,
    size: "",
    code: ""
});

export interface UserProductFilterModel {
    startDate?: Date;
    endDate?: Date;
    search: string | undefined;
    pageNumber: number;
    pageSize: number;
    sortModel: SortModel;
    pagingDisabled: boolean;
    searchOption: ProductSearchOption;
    feature: ProductFeatureType;
    menuFilters: MenuFilterModel[];
    shopBy: MenuFilterModel[];
}

export const createUserProductFilterModel = (): UserProductFilterModel => ({
    startDate: undefined,
    endDate: undefined,
    search: "",
    pageNumber: 1,
    pageSize: 50,
    sortModel: new SortModel(),
    pagingDisabled: false,
    searchOption: ProductSearchOption.contains,
    feature: ProductFeatureType.none,
    menuFilters: [],
    shopBy: []
});

export interface ProductFilterModel extends UserProductFilterModel {
    deletedOnly: BooleanOption;
    activeOnly: BooleanOption;
    approvedOnly: BooleanOption;
    level: CustomerLevel;
    inStock: BooleanOption;
    noCategory: boolean;
    noEarningPoint: boolean;
    noBarcode: boolean;
    sizeId?: number;
    warehouseId: number;
}

export const createProductFilterModel = (): ProductFilterModel => ({
    startDate: undefined,
    endDate: undefined,
    search: "",
    pageSize: 50,
    pageNumber: 1,
    sortModel: new SortModel(),
    pagingDisabled: false,
    searchOption: ProductSearchOption.contains,
    feature: ProductFeatureType.none,
    menuFilters: [],
    shopBy: [],
    deletedOnly: BooleanOption.none,
    activeOnly: BooleanOption.none,
    approvedOnly: BooleanOption.none,
    level: CustomerLevel.none,
    inStock: BooleanOption.none,
    noCategory: false,
    noEarningPoint: false,
    noBarcode: false,
    sizeId: undefined,
    warehouseId: 0
});

export enum HomePageLinkType {
    all,
    topStory,
    category,
    brand,
    topProduct,
    quickLink,
    favorite,
    recentView,
    specialSale,
    topRate,
    topSale,
    newArrival,
    choice,
    none
}

export interface HomePageLinkInfo {
    id: number;
    type?: HomePageLinkType;
    name?: string;
    description?: string;
    path?: string;
    settings?: string;
    fromDate: Date;
    toDate: Date;
    order: number;
    isMobile: boolean;
    picture?: PictureModel;
}

export const createHomePageLinkInfo = (): HomePageLinkInfo => ({
    id: 0,
    type: undefined,
    name: "",
    description: "",
    path: "",
    settings: "",
    fromDate: new Date(0),
    toDate: new Date(0),
    order: 0,
    isMobile: false,
    picture: undefined
});

export interface HomePageLinkFilterModel {
    type?: HomePageLinkType
    option: SearchOption;
    key: string;
    search: string;
}

export const createHomePageLinkFilterModel = (): HomePageLinkFilterModel => ({
    type: undefined,
    option: SearchOption.contains,
    key: "",
    search: ""
});

export interface ProductKeyModel {
    groupId: number;
    lineId: number;
    productId: number;
}

export const createProductKeyModel = (): ProductKeyModel => ({
    groupId: 0,
    lineId: 0,
    productId: 0
});

export interface ProductCountByModel {
    brands: any;
    categories: any;
}

export const createProductCountByModel = (): ProductCountByModel => ({
    brands: {},
    categories: {}
});

export enum SizeType {
    letter, number
}

export interface SizeModel {
    id: number;
    name: string;
    nickName?: string;
    value: number;
    type: SizeType;
}

export const createSizeModel = (): SizeModel => ({
    id: 0,
    name: "",
    nickName: "",
    value: 0,
    type: SizeType.number
});

export interface ColorModel {
    id: number;
    code: string;
    name: string;
    nickName: string;
}

export const createColorModel = (): ColorModel => ({
    id: 0,
    code: "",
    name: "",
    nickName: ""
});

export enum PreferenceType {
    like, favorite, priceChange, recentView, recentOrder, shared
}

export interface PreferenceModel {
    id: number;
    customerId: number;
    lineId: number;
    referenceId?: number;
    type: PreferenceType;
    date: Date;
}

export const creatPreferenceModel = (): PreferenceModel => ({
    id: 0,
    customerId: 0,
    lineId: 0,
    referenceId: undefined,
    type: PreferenceType.like,
    date: new Date(0)
});

export interface BrandModel {
    id: number;
    name: string;
    nickName: string;
    folderName: string;
}

export const createBrandModel = (): BrandModel => ({
    id: 0,
    name: "",
    nickName: "",
    folderName: ""
});

export interface ShortProductModel {
    id: number;
    barcode: string;
    size: string;
    code: string;
    lineId: number;
    lineName: string;
    style: string;
    groupId: number;
    productName: string;
    brandId: number;
    brand: string;
    imageUrl?: string;
    salePrice: number;
    cost: number;
}

export interface ProductModel {
    id: number;
    lineId: number;
    lineName: string;
    msrp: number;
    price: number;
    shipping: number;
    fee: number;
    sizeId?: number;
    inStock: number;
    active: boolean;
    deleted: boolean;
    codeOrder: number;
    code?: string;
    barcode?: string;
    size: SizeModel;
    recordInfo: RecordInfo;
    printInfo: ProductPrintModel;
    promotionIds: number[];
    inventory: any; //InventoryInfoWrapper
    lastCoundDate?: Date;
}

export const createProductModel = (): ProductModel => ({
    id: 0,
    lineId: 0,
    lineName: "",
    msrp: 0,
    price: 0,
    shipping: 0,
    fee: 0,
    sizeId: undefined,
    inStock: 0,
    active: false,
    deleted: false,
    codeOrder: 0,
    code: undefined,
    barcode: undefined,
    size: createSizeModel(),
    recordInfo: createRecordInfo(),
    printInfo: createProductPrintModel(),
    promotionIds: [],
    inventory: {}, //InventoryInfoWrapper
    lastCoundDate: undefined
});

export interface ProductLineModel {
    id: number;
    name: string;
    style: string;
    code?: string;
    deleted: boolean;
    active: boolean;
    approved: boolean;
    newArrivalDate: Date;
    priceDisplayType: PriceDisplayType;
    feature: ProductFeatureModel;
    isFavorite: boolean;
    imageUrl?: string;
    products: ProductModel[];
    representedColors: ColorModel[];
    recordInfo: RecordInfo;
    preferences: PreferenceModel[];
}

export const createProductLineModel = (): ProductLineModel => ({
    id: 0,
    name: "",
    style: "",
    code: "",
    deleted: false,
    active: false,
    approved: false,
    newArrivalDate: new Date(0),
    priceDisplayType: PriceDisplayType.show,
    feature: createProductFeatureModel(),
    isFavorite: false,
    imageUrl: undefined,
    products: [],
    representedColors: [],
    recordInfo: createRecordInfo(),
    preferences: []
});

export interface GroupDetailModel {
    id: number;
    name: string;
    level: CustomerLevel;
    specification: string;
    note?: string;
    categories: MenuFilterModel[];
    brand: BrandModel;
    earningPoints: EarningPointModel[];
    lines: ProductLineModel[];
    totalPointRate: number;
}

export const createGroupDetailModel = (): GroupDetailModel => ({
    id: 0,
    name: "",
    level: CustomerLevel.invited,
    specification: "",
    note: "",
    categories: [],
    brand: createBrandModel(),
    earningPoints: [],
    lines: [],
    totalPointRate: 0
});

export interface RecommendedProductFilterModel {
    token: string;
    type: ProductFeatureType;
    maxCount: number;
}